// Pure helpers for the run-report surface (A2UI catalog `analysis/v1`).
// The catalog, the data-model shape and the sizing rules come from the server
// (agent-server `core/tools/a2ui_catalogs/analysis_v1.json`) and the design
// (docs/design/32-run-report.md). Rendering lives in components/RunReportComponents.tsx.

export const CATALOG_ID_ANALYSIS = 'pg.meninblox.dev:analysis/v1';

/* eslint-disable @typescript-eslint/no-explicit-any -- the data model is schema-driven dynamic JSON */
export type DataModel = Record<string, any>;

/** Look up a JSON-pointer-style path (`/splits/rows`) in the surface data model. */
export const resolvePath = (dataModel: DataModel | undefined, path: string): any => {
  let cur: any = dataModel;
  for (const key of path.split('/').filter(Boolean)) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[key];
  }
  return cur;
};

/** A2UI DynamicString / DataBinding: a literal, `{path}` into the data model, or `{literalString}`. */
export const resolveValue = (value: any, dataModel: DataModel | undefined): any => {
  if (value == null) return undefined;
  if (typeof value !== 'object' || Array.isArray(value)) return value;
  if (typeof value.path === 'string') return resolvePath(dataModel, value.path);
  if ('literalString' in value) return value.literalString;
  return undefined;
};

/** Text for display — empty string when the binding points at nothing, so callers can omit the node. */
export const resolveText = (value: any, dataModel: DataModel | undefined): string => {
  const v = resolveValue(value, dataModel);
  return v == null ? '' : String(v);
};

/** Resolve a binding that must be a list (`series`, `rows`); anything else is an empty list. */
export const resolveList = (value: any, dataModel: DataModel | undefined): any[] => {
  const v = resolveValue(value, dataModel);
  return Array.isArray(v) ? v : [];
};
/* eslint-enable @typescript-eslint/no-explicit-any */

const isNum = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

/** Map `v` from [lo, hi] onto [outLo, outHi]; a flat range lands in the middle. */
const scale = (v: number, lo: number, hi: number, outLo: number, outHi: number): number =>
  hi === lo ? (outLo + outHi) / 2 : outLo + ((v - lo) / (hi - lo)) * (outHi - outLo);

/**
 * PairBars heights, 30–64px. Pace: faster (smaller paceSec) is taller. HR: higher is taller.
 * A missing value yields null so the column keeps its slot without a bar.
 */
export const pairBarHeights = (
  series: { paceSec?: number | null; hr?: number | null }[],
): { pace: number | null; hr: number | null }[] => {
  const paces = series.map((s) => s.paceSec).filter(isNum);
  const hrs = series.map((s) => s.hr).filter(isNum);
  const [pMin, pMax] = [Math.min(...paces), Math.max(...paces)];
  const [hMin, hMax] = [Math.min(...hrs), Math.max(...hrs)];
  return series.map((s) => ({
    pace: isNum(s.paceSec) ? scale(pMax - s.paceSec, 0, pMax - pMin, 30, 64) : null,
    hr: isNum(s.hr) ? scale(s.hr, hMin, hMax, 30, 64) : null,
  }));
};

/** DistBars height: distanceKm / max × 56. */
export const distBarHeight = (km: number, maxKm: number): number => (maxKm > 0 ? Math.max(4, (km / maxKm) * 56) : 4);

/** CountBars height: 14 + count × 14. */
export const countBarHeight = (count: number): number => 14 + Math.max(0, count) * 14;

/** HBarRows bar length as a % of the track, 40–95. Faster (smaller paceSec) is longer. */
export const hbarPercents = (rows: { paceSec?: number | null }[]): number[] => {
  const paces = rows.map((r) => r.paceSec).filter(isNum);
  const [lo, hi] = [Math.min(...paces), Math.max(...paces)];
  return rows.map((r) => (isNum(r.paceSec) ? scale(hi - r.paceSec, 0, hi - lo, 40, 95) : 40));
};

/** ElevBars bar height in a 30px half-lane (min 4). Numbers only show from |elev| ≥ 8. */
export const elevBar = (elev: number, maxAbs: number): { up: boolean; height: number; label: string } => ({
  up: elev >= 0,
  height: maxAbs > 0 ? Math.max(4, (Math.abs(elev) / maxAbs) * 30) : 4,
  label: Math.abs(elev) >= 8 ? `${elev > 0 ? '+' : ''}${Math.round(elev)}` : '',
});

export type GaugeZone = 'low' | 'good' | 'high';

/** LoadGauge knob position (0–1) and zone: low < 30% ≤ good < 65% ≤ high. */
export const gaugePosition = (level: number, max: number): { ratio: number; zone: GaugeZone } => {
  const ratio = max > 0 ? Math.min(1, Math.max(0, level / max)) : 0;
  return { ratio, zone: ratio < 0.3 ? 'low' : ratio < 0.65 ? 'good' : 'high' };
};

/** `POST /chats` `surface_action` body — mirrors agent-server `SurfaceAction`. */
export interface SurfaceActionPayload {
  name: 'select' | 'open_run_report';
  surface_id?: string;
  source_component_id?: string;
  context: Record<string, unknown>;
}

/**
 * A card tap → the server's `surface_action`, or null when the event is one the server
 * doesn't route (Button actions like `open_pain_intake` open app screens, not a chat turn).
 * `open_more` is expanded locally by the app from `dataModel.more.items`; the dashboard has no
 * such list UI, so it asks the server for the legacy `more` card instead (still supported).
 */
export const toSurfaceAction = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI action event
  event: any,
  surfaceId: string | undefined,
  componentId: string,
): SurfaceActionPayload | null => {
  if (event?.name === 'open_more') {
    return {
      name: 'select',
      ...(surfaceId ? { surface_id: surfaceId } : {}),
      source_component_id: componentId,
      context: { id: 'more' },
    };
  }
  const name = event?.name;
  if (name !== 'select' && name !== 'open_run_report') return null;
  return {
    name,
    ...(surfaceId ? { surface_id: surfaceId } : {}),
    source_component_id: componentId,
    context: { ...(event.context ?? {}) },
  };
};
